import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, getIdTokenResult, signOut } from 'firebase/auth';
import { auth, functions } from '../firebase/config';
import { 
    signUp, 
    logIn, 
    logOut, 
    resetPassword, 
    resendVerificationEmail,
    validatePhilippineNumber
} from '../firebase/auth';

// NEW: Added missing Firestore imports
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { isFirebaseConfigured, db } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { normalizeUserRole, USER_ROLES } from '../utils/adminRoles';

/**
 * Authentication context for managing user authentication state.
 */
const AuthContext = createContext(null);

/**
 * Custom hook to use the authentication context.
 * @returns {Object} Authentication context value
 */
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

/**
 * Authentication provider component that wraps the app and provides auth state.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    // Subscribe to auth state changes
    useEffect(() => {
        let isMounted = true;

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (!isMounted) return;
            setCurrentUser(user);
            setLoading(true);

            if (user && isFirebaseConfigured && db) {
                try {
                    let resolvedRole = USER_ROLES.CUSTOMER;
                    await getIdTokenResult(user, true);
                    const adminDocRef = doc(db, 'admins', user.uid);
                    let adminDocSnap = await getDoc(adminDocRef);
                    const adminStatus = adminDocSnap.exists() ? adminDocSnap.data()?.status : null;

                    if (adminStatus === 'invited' && functions) {
                        const completeAdminInvite = httpsCallable(functions, 'completeAdminInvite');
                        await completeAdminInvite({});
                        await getIdTokenResult(user, true);
                        adminDocSnap = await getDoc(adminDocRef);
                    }

                    const userDocRef = doc(db, 'users', user.uid);
                    const userDocSnap = await getDoc(userDocRef);
                    const adminData = adminDocSnap.exists() ? adminDocSnap.data() : null;
                    const userData = userDocSnap.exists() ? userDocSnap.data() : null;
                    const hasUserProfile = userDocSnap.exists();
                    const hasAdminRecord = adminDocSnap.exists();

                    if (!hasUserProfile && !hasAdminRecord) {
                        await signOut(auth);
                        if (!isMounted) return;
                        setCurrentUser(null);
                        setUserRole(null);
                        setLoading(false);
                        return;
                    }

                    const hasLegacyOrActiveAdminRecord = Boolean(adminData) && (
                        adminData?.status === 'active'
                        || (adminData?.status == null
                            && normalizeUserRole(adminData?.role || adminData?.assignedRole || adminData?.invitedRole) !== USER_ROLES.CUSTOMER)
                    );
                    const activeAdminRole = hasLegacyOrActiveAdminRecord
                        ? (adminData?.role || adminData?.assignedRole || adminData?.invitedRole || userData?.role)
                        : null;

                    resolvedRole = normalizeUserRole(activeAdminRole || USER_ROLES.CUSTOMER);
                    setUserRole(resolvedRole);
                } catch (error) {
                    console.error('Failed to fetch user role:', error);
                    setUserRole(USER_ROLES.CUSTOMER);
                }
            } else {
                setUserRole(null);
            }

            setLoading(false);
        });

        // Cleanup subscription on unmount
        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []);



    /**
     * Register a new user
     */
    async function handleSignUp(email, password, userData) {
        return await signUp(email, password, userData);
    }

    /**
     * Log in an existing user
     */
    async function handleLogIn(email, password) {
        return await logIn(email, password);
    }

    /**
     * Log out the current user
     */
    async function handleLogOut() {
        return await logOut();
    }
    
    /**
     * Send password reset email
     */
    async function handleResetPassword(email) {
        return await resetPassword(email);
    }

    /**
     * Resend verification email
     */
    async function handleResendVerificationEmail(email, password) {
        return await resendVerificationEmail(email, password);
    }

    // NEW: Added getUserProfile function to fetch full user data from Firestore
    const getUserProfile = useCallback(async (userId) => {
        if (!isFirebaseConfigured || !db || !userId) {
            return null;
        }
        try {
            const userDocRef = doc(db, 'users', userId);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
                return userDocSnap.data(); // Returns { firstName, lastName, email, contactNumber, address, savedAddresses, emailNotifications, ... }
            }
            return null;
        } catch (error) {
            console.error('Failed to fetch user profile:', error);
            return null;
        }
    }, []);

    // NEW: Added updateUserProfile function to update user data in Firestore
    const updateUserProfile = useCallback(async (userId, updates) => {
        if (!isFirebaseConfigured || !db || !userId) {
            throw new Error('Firebase is not configured or user not found.');
        }
        try {
            const userDocRef = doc(db, 'users', userId);
            await updateDoc(userDocRef, {
                ...updates,
                updatedAt: serverTimestamp() // Optional: Track when it was updated
            });
            return { error: null };
        } catch (error) {
            console.error('Failed to update user profile:', error);
            return { error: 'Failed to update profile. Please try again.' };
        }
    }, []);

    const value = {
        currentUser,
        userRole,
        loading,
        signUp: handleSignUp,
        logIn: handleLogIn,
        logOut: handleLogOut,
        resetPassword: handleResetPassword,
        sendPasswordResetEmail: handleResetPassword, // Added: alias used by AuthModal's Forgot Password form
        resendVerificationEmail: handleResendVerificationEmail,
        validatePhilippineNumber,
        getUserProfile,
        updateUserProfile  // NEW: Exposed here
    };
    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}